// Column defs for the dial-list entries grid (v2 ListDetailView).
//
// Entries are denormalised: row = DialListEntry, the linked Contact
// lives on the context as `contactsById[entry.contactId]`. Lookups
// happen in the cell renderer rather than pre-joining so the parent
// can hydrate contacts lazily and the columns stay plain data.

import { Link } from 'react-router-dom';
import type { Contact, DialListEntry, DialListEntryStatus } from '@personical/cc-shared-types';
import { DIAL_LIST_ENTRY_STATUSES } from '@personical/cc-shared-types';
import { Avatar } from '../../components/Avatar.js';
import { EditableText } from '../EditableCell.js';
import type { GridColumn } from '../types.js';

export interface DialEntryGridContext {
  contactsById: Record<string, Contact>;
  patchEntry(
    entryId: string,
    patch: { status?: DialListEntryStatus; notes?: string; recordAttempt?: boolean }
  ): Promise<void>;
  removeEntry(entryId: string): Promise<void>;
}

export const STATUS_LABEL: Record<DialListEntryStatus, string> = {
  pending: 'Pending',
  in_progress: 'Dialing',
  reached: 'Reached',
  no_answer: 'No answer',
  voicemail: 'Voicemail',
  rejected: 'Rejected',
  wrong_number: 'Wrong number',
  do_not_call: 'Do not call',
  skipped: 'Skipped'
};

const STATUS_TONE: Record<DialListEntryStatus, string> = {
  pending: 'text-mid',
  in_progress: 'text-sky-700 dark:text-sky-300',
  reached: 'text-emerald-700 dark:text-emerald-300',
  no_answer: 'text-amber-700 dark:text-amber-300',
  voicemail: 'text-amber-700 dark:text-amber-300',
  rejected: 'text-red-700 dark:text-red-300',
  wrong_number: 'text-red-700 dark:text-red-300',
  do_not_call: 'text-red-700 dark:text-red-300',
  skipped: 'text-faint'
};

function NameCell({ row, ctx }: { row: DialListEntry; ctx: DialEntryGridContext }) {
  const contact = ctx.contactsById[row.contactId];
  if (!contact) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <Avatar name="—" size="sm" />
        <span className="text-faint truncate">Contact removed</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar name={contact.displayName} photoUrl={contact.photoUrl} size="sm" />
      <Link
        to={`/people/${contact.id}`}
        className="font-medium text-strong hover:underline truncate"
        onClick={(e) => e.stopPropagation()}
      >
        {contact.displayName}
      </Link>
    </div>
  );
}

function PhoneCell({ row, ctx }: { row: DialListEntry; ctx: DialEntryGridContext }) {
  const contact = ctx.contactsById[row.contactId];
  const primary = contact?.phoneNumbers?.[0];
  if (!primary) return <span className="text-faint">—</span>;
  const display = primary.e164 ?? primary.number;
  return (
    <a
      href={`tel:${display}`}
      className="text-mid hover:text-strong hover:underline truncate block tabular-nums"
      onClick={(e) => {
        e.stopPropagation();
        void ctx.patchEntry(row.id, { recordAttempt: true, status: 'in_progress' });
      }}
    >
      {display}
    </a>
  );
}

function StatusCell({ row, ctx }: { row: DialListEntry; ctx: DialEntryGridContext }) {
  return (
    <select
      value={row.status}
      onChange={(e) => {
        void ctx.patchEntry(row.id, { status: e.target.value as DialListEntryStatus });
      }}
      onClick={(e) => e.stopPropagation()}
      className={`text-sm bg-transparent border border-input rounded-md px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[color:var(--cc-ring)] ${STATUS_TONE[row.status]}`}
    >
      {DIAL_LIST_ENTRY_STATUSES.map((s) => (
        <option key={s} value={s}>
          {STATUS_LABEL[s]}
        </option>
      ))}
    </select>
  );
}

function NotesCell({ row, ctx }: { row: DialListEntry; ctx: DialEntryGridContext }) {
  return (
    <EditableText
      value={row.notes ?? ''}
      onSave={(next) => ctx.patchEntry(row.id, { notes: next })}
      ariaLabel={`Notes for entry ${row.id}`}
      display={(v) =>
        v ? (
          <span className="text-mid italic truncate block">{v}</span>
        ) : (
          <span className="text-faint">— add notes</span>
        )
      }
    />
  );
}

function AttemptsCell({ row }: { row: DialListEntry }) {
  return <span className="tabular-nums text-mid">{row.attempts}</span>;
}

function LastAttemptCell({ row }: { row: DialListEntry }) {
  if (!row.lastAttemptAt) return <span className="text-faint">—</span>;
  const d = new Date(row.lastAttemptAt);
  if (Number.isNaN(d.getTime())) return <span className="text-faint">—</span>;
  return <span className="text-mid tabular-nums">{d.toLocaleDateString()}</span>;
}

export const DIAL_ENTRY_COLUMNS: GridColumn<DialListEntry, DialEntryGridContext>[] = [
  { id: 'name', label: 'Contact', width: '18rem', render: (row, ctx) => <NameCell row={row} ctx={ctx} /> },
  { id: 'phone', label: 'Phone', width: '11rem', mobile: 'hide', render: (row, ctx) => <PhoneCell row={row} ctx={ctx} /> },
  { id: 'status', label: 'Status', width: '10rem', render: (row, ctx) => <StatusCell row={row} ctx={ctx} /> },
  { id: 'notes', label: 'Notes', mobile: 'hide', render: (row, ctx) => <NotesCell row={row} ctx={ctx} /> },
  { id: 'attempts', label: 'Attempts', width: '5rem', mobile: 'hide', render: (row) => <AttemptsCell row={row} /> },
  { id: 'last', label: 'Last attempt', width: '8rem', mobile: 'hide', render: (row) => <LastAttemptCell row={row} /> }
];
